<template lang="pug">
aside-modal#tax-form-modal(width="w-xxxl", :loading="onLoading('save-tax')")
    span(slot="header-text") {{ taxExists ? 'Update' : 'Create' }} Tax
    div.p-a(slot="body")
        form#tax-form
            fieldset(:disabled="onLoading('save-tax')")
                .form-group
                    label.form-control-label Name
                    input.form-control(
                        type="text",
                        :value="tax.name",
                        @input="setTaxValue($event.target.value, 'name')",
                        placeholder="Service Tax"
                        required
                    )

                .form-group
                    label.form-control-label Short Name
                    input.form-control(
                        type="text",
                        :value="tax.short_name",
                        @input="setTaxValue($event.target.value, 'short_name')"
                        placeholder="SST"
                    )

                .form-group
                    label.form-control-label Amount (%)
                    input.form-control(
                        type="text",
                        data-parsley-type="number",
                        placeholder="0",
                        required,
                        @input="setTaxValue($event.target.value, 'amount')",
                        :value="tax.amount"
                    )

                .form-group
                    label.form-control-label Application Mode
                    multiselect(
                        name="type"
                        label="name"
                        track-by="value"
                        v-model="modeMultiselect"
                        :allow-empty="false"
                        :close-on-select="true"
                        :multiple="false"
                        :options="applicationModes"
                        :show-labels="false"
                    )

                .form-group
                    label.form-control-label Amount Mode
                    multiselect(
                        name="type"
                        label="name"
                        track-by="value"
                        v-model="inclusiveMultiselect"
                        :allow-empty="false"
                        :close-on-select="true"
                        :multiple="false"
                        :options="amountModes"
                        :show-labels="false"
                    )

                .form-group
                    span.pull-right {{ products.length }} of {{ productList.length }}
                    label.form-control-label Products
                    multiselect(
                        v-model="products",
                        label="name",
                        track-by="id",
                        placeholder="Type to search",
                        :options="productList",
                        :multiple="true",
                        :loading="onLoading('fetch-product')",
                        :close-on-select="false",
                        :limit="30"
                    )

                    button.btn.btn-sm.btn-block.white.m-t-1(
                        v-if="!allProductsHasAsigned"
                        type="button"
                        @click="assignAllProducts"
                    )
                        | Assign tax to all products

                div.form-group
                    label Options
                    div.form-group
                        p
                            label.md-switch
                                input.has-value(
                                    type="checkbox",
                                    name="assign-outlet",
                                    @change="setTaxValue($event.target.checked, 'auto_assign_outlet')",
                                    :checked="tax.auto_assign_outlet"
                                )
                                i.indigo
                                | Auto assign to outlet
                        p
                            small Automatically assign tax when new outlet created
                
                div.form-group
                    p
                        label.md-switch
                            input.has-value(
                                type="checkbox",
                                name="auto_assign_product",
                                @change="setTaxValue($event.target.checked, 'auto_assign_product')",
                                :checked="tax.auto_assign_product"
                            )
                            i.indigo
                            | Auto assign to product
                    p
                        small Automatically assign tax when new product is created

    button.btn.btn-fw.primary(
        slot="footer"
        @click="submit"
        :disabled="onLoading('save-tax') || !canManageTaxes"
    )
        v-loading(loader="save-tax") Save
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PermissionMixin from '../tax-permission';

export default {
    mixins: [PermissionMixin],

    computed: {
        ...mapGetters('tax', ['tax']),

        ...mapGetters('product', {
            productList: 'products',
        }),

        taxExists() {
            return this.tax.id !== null;
        },

        allProductsHasAsigned() {
            return this.products.length == this.productList.length;
        },

        products: {
            get() {
                return this.tax.products;
            },
            set(value) {
                this.setTaxValue(value, 'products');
            },
        },

        amountModes() {
            return [
                { name: 'Include tax in product price', value: true },
                { name: 'Add tax to product price', value: false },
            ];
        },

        applicationModes() {
            return [
                { name: 'Apply on subtotal amount', value: 0 },
                { name: 'Apply on every product amounts', value: 1 },
            ];
        },

        inclusiveMultiselect: {
            get() {
                if (this.tax == null) return false;

                let m = this.amountModes.find(
                    x => x.value == this.tax.inclusive,
                );

                if (m == undefined) {
                    m = this.amountModes[1];
                }

                return m;
            },

            set(value) {
                this.setTaxValue(value.value, 'inclusive');
            },
        },

        modeMultiselect: {
            get() {
                if (this.tax == null) return null;

                let m = this.applicationModes.find(
                    x => x.value == this.tax.mode,
                );

                if (m == undefined) {
                    m = this.applicationModes[0];
                }

                return m;
            },

            set(value) {
                this.setTaxValue(value.value, 'mode');
            },
        },
    },

    mounted() {
        this.registerEvent();
        this.getProductList({
            limit: null,
            query: null,
            relation: 0,
        });
    },

    methods: {
        ...mapActions('tax', [
            'setTaxAttribute',
            'getTax',
            'createTax',
            'updateTax',
        ]),

        ...mapActions('product', ['getProductList']),

        setTaxValue(value, key) {
            this.setTaxAttribute({ key, value });
        },

        submit() {
            let submit;

            if (!this.validateForm('tax-form')) return;

            this.startLoading('save-tax');

            if (this.taxExists) {
                submit = this.updateTax();
            } else {
                submit = this.createTax();
            }

            submit
                .then(res => {
                    this.showToastrSuccess();
                    this.endLoading('save-tax');

                    if (!this.taxExists) this.resetTaxForm();
                    else $('#tax-form-modal').modal('hide');

                    this.$emit('tax-saved');
                })
                .catch(err => {
                    this.endLoading('save-tax');
                    this.showApiError(err);
                    throw err;
                });
        },

        resetTaxForm() {
            this.setTaxValue(null, 'id');
            this.setTaxValue(null, 'name');
            this.setTaxValue(null, 'short_name');
            this.setTaxValue(null, 'amount');
            this.setTaxValue(0, 'mode');
            this.setTaxValue(false, 'inclusive');
            this.setTaxValue(true, 'auto_assign_outlet');
            this.setTaxValue(true, 'auto_assign_product');
            this.setTaxValue([], 'products');
        },

        registerEvent() {
            this.$on('show-form', id => {
                if (id !== null) {
                    this.getTax(id).then(tax => {
                        $('#tax-form-modal').modal('show');
                    });
                } else {
                    this.resetTaxForm();
                    $('#tax-form-modal').modal('show');
                }
            });
        },

        assignAllProducts() {
            this.setTaxValue(this.productList, 'products');
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/tax/tax_form.vue